import { CommonModule } from '../common.module';
import { ExtensionContext } from "../../common/models/extension-config";
import { Module } from "../../common/module";
import { inject, injectable } from 'tsyringe';
import { Stack } from '../../common/data-structures/stack';
import { RtfCss } from './rtfcss';

@injectable()
export class RtfDisplayModule extends Module {
    public name: string = 'RtfDisplayModule';

    private readonly specialChar: string = '¨';
    constructor(@inject(CommonModule) private common: CommonModule) {
        super([common]);
    }

    public acceptedLicenseFlags: string[] = [];
    public shouldRun(context: ExtensionContext): boolean { return context == ExtensionContext.vizql; }
    public async run(): Promise<void> {
        const body = await this.common.waitForElement('body', 30, document.documentElement);
        if (!body) {
            console.error('RtfDisplayModule did not find body, not running');
            return;
        }
        const observer = new MutationObserver(this.observerCallback.bind(this));
        observer.observe(body, {
            subtree: true,
            childList: true,
            attributes: false
        });
        // add Quill CSS to VizQL page
        RtfCss.injectCss(document, body);
        // check the initial document, and replace the HTMLs (in case MutationObserver was not running when it was rendered)
        this.replaceRtfForElement(body);
    }

    private observerCallback(mutations: MutationRecord[]) {
        for (let i = 0; i < mutations.length; i++) {
            const mutation = mutations[i];
            if (mutation.addedNodes.length === 0) {
                continue;
            }
            for (let i2 = 0; i2 < mutation.addedNodes.length; i2++) {
                if (
                    !mutation.addedNodes[i2].innerHTML ||
                    !mutation.addedNodes[i2].innerHTML.includes(this.specialChar)
                ) {
                    continue;
                }
                this.replaceRtfForElement(mutation.addedNodes[i2] as Element);
            }
        }
    }

    private readonly normalizedHtmlTags: any = {
        "<p>": "<span>",
        "<div>": "<span>",
        "</p>": "</span></br>",
        "</div>": "</span></br>",
    };

    private replaceRtfForElement(el: Element) {
        if (!el) {
            return;
        }
        const maxRuns = el.innerText.split(this.specialChar).length - 1;
        let actualRuns = 0;
        //console.log('replace for el', el);
        while (el.innerText.includes(this.specialChar) && actualRuns < maxRuns) {
            actualRuns++;
            const lowestChild = this.findLowestChildWithInnerText(this.specialChar, el);
            if (!lowestChild) {
                break;
            }
            // build:
            // - prefix text
            // - all custom HTML texts (might be multiple). we build these using a custom parser
            // - suffix text
            // we call these text 'components'
            // if there , replace for the HTML itself
            // else replace with <span>s for all text components
            const components: string[] = [];
            const str = lowestChild.innerText.trim();
            let strIndex: number = 0;
            const maxInnerRuns = str.split(this.specialChar).length - 1;
            let actualInnerRuns = 0;
            while (str.includes(this.specialChar, strIndex) && actualInnerRuns < maxInnerRuns) {
                actualInnerRuns++;
                const i = str.indexOf(this.specialChar, strIndex);
                if (i !== 0) {
                    const prefix = str.substring(strIndex, i);
                   // console.log('Adding prefix: ' + prefix)
                    components.push(prefix);
                }
                strIndex = i + 1;
                if (str.length <= strIndex) {
                    throw new Error('Unexpected end of HTML string (after special character)');
                }
                // special char is now index 0, start parsing HTML from index 1
                // the HTML may be partial, and there may be other text components after it.
                // we parse it, decide where it's done, and treat every other following text as a different component
                // if it finishes prematurely, then the HTML content has been truncated, finish the HTML (based on the parser) and provide a custom error truncated icon and add it as a component
                const htmlStack = new Stack<string>();
                let htmlComponent = '';
                //console.log('Building HTML Stack');
                do {
                    let nextSpecialCharIndex = str.indexOf(this.specialChar, strIndex);
                    if (nextSpecialCharIndex === -1) {
                        nextSpecialCharIndex = Infinity;
                    }
                    // read HTML string until next '<'
                    const openTagIndex = str.indexOf('<', strIndex);
                   // console.log('Found openTagIndex at ' + openTagIndex);
                    // truncated string. jump to index of next special char or end of string
                    if (openTagIndex === -1 || openTagIndex > nextSpecialCharIndex) {
                        strIndex = nextSpecialCharIndex === Infinity ? str.length: nextSpecialCharIndex;
                       // console.log(`openTagIndex is -1 or after next special char index: ${nextSpecialCharIndex}. new strIndex: ${strIndex}`)
                        break;
                    }
                   
                    const closeTagIndex = str.indexOf('>', strIndex);
                  // truncated string. jump to index of next special char or end of string
                    if (closeTagIndex === -1 || closeTagIndex > nextSpecialCharIndex) {
                        strIndex = nextSpecialCharIndex === Infinity ? str.length: nextSpecialCharIndex;
                       // console.log(`closeTagIndex is -1 or after next special char index: ${nextSpecialCharIndex}. new strIndex: ${strIndex}`)
                        break;
                    }
                    //console.log('Found closeTagIndex at ' + closeTagIndex);
                    htmlComponent += str.substring(strIndex, openTagIndex + 1);
                    strIndex = openTagIndex + 1;
                    htmlComponent += str.substring(strIndex, closeTagIndex + 1);
                    strIndex = closeTagIndex + 1;

                    if (closeTagIndex <= openTagIndex) {
                        // something is wrong
                        throw new Error('Parsed HTML string has > before <');
                    }

                    const isClose = str[openTagIndex + 1] === '/';
                    const tagName = /<\/?([a-zA-Z]+)[^>]*>/m.exec(str.substring(openTagIndex, closeTagIndex + 1))![1];
                    if (!this.selfClosingTags.includes(tagName.toLowerCase())) {
                       
                        // if this is a tag open
                        if (!isClose) {
                            // add a tag with this name to the stack
                            htmlStack.push(tagName);
                          // console.log('Name: ' + tagName, "Close: ", isClose, " added to stack");
                        } else {
                            // check the last item in the dictionary
                            const p = htmlStack.pop();
                            if (p != tagName) {
                                throw new Error(`Parsed HTML contains a </${tagName}>, but a </${p}> is expected. Full string: ` + str);
                            }
                           // console.log('Name: ' + tagName, "Close: ", isClose, " removed from stack");
                        }
                    }
                    // we work until the stack is empty or next character is not a tag open (then the HTML continues)
                } while (htmlStack.size() > 0 || (str.length >= strIndex && str[strIndex] === '<'));
                let normalizedHtml = htmlComponent.trim();
                Object.keys(this.normalizedHtmlTags).forEach(tag => normalizedHtml = normalizedHtml.split(tag).join(this.normalizedHtmlTags[tag]));
                if (normalizedHtml.endsWith("</br>")) {
                    normalizedHtml = normalizedHtml.substring(0, normalizedHtml.length - 5);
                }

                // we had a parse error
                if (htmlStack.size() > 0) {
                    while (htmlStack.size() > 0) {
                        //console.log('Finishing truncated HTML component');
                        const tag = `</${htmlStack.pop()}>`;
                        normalizedHtml += this.normalizedHtmlTags[tag] ?? tag;
                    }
                    if (normalizedHtml.endsWith("</br>")) {
                        normalizedHtml = normalizedHtml.substring(0, normalizedHtml.length - 5);
                    }
                    normalizedHtml += `<span class="elements-truncated">
                        <span class="truncate-dots">...</span>
                        <span class="truncate-icon">${RtfCss.truncatedIcon}</span>
                    </span>`;
                }
                
              
                
                //console.log('Returning HTML component: ' + normalizedHtml);
                components.push(normalizedHtml);
            }
            // no more special characters -> everything else is suffix
            let suffix = str.length > strIndex ? str.substring(strIndex).trim() : '';
            if (suffix.includes(this.specialChar)) {
                suffix = suffix.substring(0, suffix.indexOf(this.specialChar));
            }
            if (suffix.length > 0) {
               // console.log('Adding suffix: ' + suffix);
                components.push(suffix);
            }

            const validComponents = components.filter(e => e?.trim().length > 0);
            let innerHtml: string;
            if (validComponents.length === 1) {
                innerHtml = validComponents[0];
            } else {
                innerHtml = validComponents.map(c => c.startsWith('<span>') && c.endsWith('</span>') ? c: `<span>${c}</span>`).join('');
            }
            //console.log(innerHtml);
            lowestChild.innerHTML = innerHtml;
            lowestChild.classList.add("ql-editor");
        }
    }

    private findLowestChildWithInnerText(innerText: string, topElement: Element): Element | undefined {
        let el = topElement;
        if (el === undefined) {
            return undefined;
        }
        for (var i = 0; i < topElement.childNodes.length; i++) {
            if (
                topElement.childNodes[i].innerHTML &&
                topElement.childNodes[i].innerHTML.includes(innerText)
            ) {
                const lowestChild = this.findLowestChildWithInnerText(
                    innerText,
                    topElement.childNodes[i] as Element
                );
                if (lowestChild !== undefined) {
                    el = lowestChild;
                    break;
                }
            }
        }
        return el;
    }
    private readonly selfClosingTags: string[] = [
        'area', 'base', 'br', 'col', 'embed', 'hr', 'img', 'input', 'link', 'meta', 'param', 'source', 'track', 'wbr'
    ]
}